import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { SurveyJSApi } from './SurveyJSApi';

export class SurveyJSApiClient implements SurveyJSApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getSurveyList() {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const response = await this.fetchApi.fetch(`${baseUrl}/surveys`);
    if (!response.ok) {
      throw new Error(`Failed to get survey list: ${response.statusText}`);
    }
    return await response.json();
  }

  async getSurvey(id: number) {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const response = await this.fetchApi.fetch(`${baseUrl}/survey/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to get survey: ${response.statusText}`);
    }
    return await response.json();
  }

  async deleteSurvey(id: number) {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const response = await this.fetchApi.fetch(`${baseUrl}/survey/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to delete survey: ${response.statusText}`);
    }
    return await response.json();
  }

  async getSurveyResponses(id: number) {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/survey/${id}/result`,
    );
    if (!response.ok) {
      throw new Error(`Failed to get survey responses: ${response.statusText}`);
    }
    return await response.json();
  }

  async getSurveySchema(id: number) {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/survey/${id}/schema`,
    );
    if (!response.ok) {
      throw new Error(`Failed to get survey schema: ${response.statusText}`);
    }
    return await response.json();
  }

  async submitSurveyResponse(id: number, response: any, userId: string) {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const res = await this.fetchApi.fetch(`${baseUrl}/survey/${id}/response`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ response, userId }),
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(
        errorData.error ||
          `Failed to submit survey response: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async saveEditedSurvey(id: number, surveyJson: any, title: string) {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const response = await this.fetchApi.fetch(`${baseUrl}/survey/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ surveyJson: surveyJson, title }),
    });
    if (!response.ok) {
      throw new Error(`Failed to save edited survey: ${response.statusText}`);
    }
    return await response.json();
  }

  async saveSurvey(surveyJson: any, title: string) {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const response = await this.fetchApi.fetch(`${baseUrl}/survey`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ surveyJson, title }),
    });
    if (!response.ok) {
      throw new Error(`Failed to save survey: ${response.statusText}`);
    }
    return await response.json();
  }

  async updateSurveyResponse(
    surveyId: number,
    responseId: number,
    response: any,
  ) {
    const baseUrl = await this.discoveryApi.getBaseUrl('surveyjs');
    const res = await this.fetchApi.fetch(
      `${baseUrl}/survey/${surveyId}/response/${responseId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ response }),
      },
    );

    if (!res.ok) {
      throw new Error(`Failed to update survey response: ${res.statusText}`);
    }

    const result = await res.json();
    return {
      id: responseId,
      ...result,
    };
  }
}
